// ZoneColorSlider.tsx - PROPER FIX
import React, { useState, useEffect, useMemo } from "react";
import { Slider, Typography } from "antd";
import { useTranslation } from "react-i18next";
import { getColorForZone } from "./soilColorUtil";
import "./CustomThresholdSlider.less";
import { formatTranslation } from "../utils/translationUtils";

const { Text } = Typography;

interface ZoneColorSliderProps {
  zones: number;
  palette: string;
  distributionStats: any;
  distributionZoneData: any[];
  onThresholdChange: (newThresholds: number[]) => void;
  thresholds?: number[];
  propertyName?: string;
}

export const ZoneColorSlider: React.FC<ZoneColorSliderProps> = ({
  zones,
  palette,
  distributionStats,
  distributionZoneData,
  onThresholdChange,
  thresholds,
  propertyName,
}) => {
  const { t } = useTranslation();
  const [currentValues, setCurrentValues] = useState<number[]>([]);
  const SLIDER_WIDTH = 420;
  const thresholdStep = useMemo(() => {
    const min = Number(distributionStats?.min);
    const max = Number(distributionStats?.max);

    if (!Number.isFinite(min) || !Number.isFinite(max) || max <= min) {
      return 0.0001;
    }

    const dynamic = (max - min) / Math.max((zones + 1) * 200, 1000);
    return Math.max(dynamic, 1e-6);
  }, [distributionStats, zones]);

  const normalizeThresholds = (
    rawThresholds: number[],
    min: number,
    max: number,
    expectedLength: number
  ): number[] => {
    if (!Number.isFinite(min) || !Number.isFinite(max) || max <= min) {
      return [];
    }

    const values = rawThresholds
      .filter((value): value is number => Number.isFinite(value))
      .sort((a, b) => a - b);

    if (values.length !== expectedLength) {
      const interval = (max - min) / (expectedLength - 1);
      return Array.from({ length: expectedLength }, (_, index) =>
        Number.parseFloat((min + interval * index).toFixed(2))
      );
    }

    const normalized = [...values];
    normalized[0] = min;
    normalized[normalized.length - 1] = max;

    for (let i = 1; i < normalized.length - 1; i++) {
      const minAllowed = normalized[i - 1] + thresholdStep;
      const maxAllowed = max - thresholdStep * (normalized.length - 1 - i);
      const clamped = Math.min(Math.max(normalized[i], minAllowed), maxAllowed);
      normalized[i] = Number.parseFloat(clamped.toFixed(6));
    }

    normalized[0] = Number.parseFloat(min.toFixed(6));
    normalized[normalized.length - 1] = Number.parseFloat(max.toFixed(6));

    return normalized;
  };

  // Calculate proper thresholds - CORRECT VERSION
  const calculatedThresholds = useMemo(() => {
    if (!distributionStats) {
      return [];
    }

    const min = Number(distributionStats.min);
    const max = Number(distributionStats.max);
    const expectedLength = zones + 1;

    if (thresholds && thresholds.length > 0) {
      return normalizeThresholds(thresholds, min, max, expectedLength);
    }

    if (distributionStats && distributionZoneData?.length > 0) {
      // For 5 zones, we need 6 thresholds: [min, z1.max, z2.max, z3.max, z4.max, z5.max]
      return normalizeThresholds(
        [
          distributionStats.min,
          ...distributionZoneData.map((zone) => zone.max),
        ],
        min,
        max,
        expectedLength
      );
    }

    return normalizeThresholds([], min, max, expectedLength);
  }, [
    distributionStats,
    distributionZoneData,
    thresholds,
    zones,
    thresholdStep,
  ]);

  // Initialize / sync current values.
  // Use approximate equality so tiny floating-point differences from
  // re-normalisation after Apply don't trigger a spurious full reset.
  useEffect(() => {
    if (calculatedThresholds?.length > 0) {
      setCurrentValues((prev) => {
        if (
          prev.length === calculatedThresholds.length &&
          prev.every(
            (value, index) =>
              Math.abs(value - calculatedThresholds[index]) < 1e-6
          )
        ) {
          return prev;
        }
        return calculatedThresholds;
      });
    }
  }, [calculatedThresholds]);

  const handleSliderChange = (values: number[]): void => {
    const fullValues = normalizeThresholds(
      [distributionStats.min, ...values, distributionStats.max],
      Number(distributionStats.min),
      Number(distributionStats.max),
      zones + 1
    );
    setCurrentValues(fullValues);
  };

  // Update parent when dragging complete
  const handleAfterChange = (values: number[]): void => {
    onThresholdChange(
      normalizeThresholds(
        [distributionStats.min, ...values, distributionStats.max],
        Number(distributionStats.min),
        Number(distributionStats.max),
        zones + 1
      )
    );
  };

  // Render the colored track segments
  const renderTrackSegments = (): React.ReactNode => {
    if (!distributionStats || currentValues.length < 2) return null;
    const range = Number(distributionStats.max) - Number(distributionStats.min);
    if (!Number.isFinite(range) || range <= 0) return null;

    return currentValues.slice(0, -1).map((value, index) => {
      const nextValue = currentValues[index + 1];
      const left = ((value - distributionStats.min) / range) * 100;
      const width = ((nextValue - value) / range) * 100;

      let borderRadius = "0";
      if (index === 0) {
        borderRadius = "4px 0 0 4px";
      } else if (index === currentValues.length - 2) {
        borderRadius = "0 4px 4px 0";
      }

      return (
        <div
          key={`segment-${value.toFixed(2)}-${nextValue.toFixed(2)}`}
          className="track-segment"
          style={{
            position: "absolute",
            left: `${left}%`,
            width: `${width}%`,
            height: "6px",
            backgroundColor: getColorForZone(
              index,
              currentValues.length - 1,
              palette
            ),
            borderRadius,
          }}
        />
      );
    });
  };

  if (!distributionStats || currentValues.length === 0) {
    return (
      <div className="zone-color-slider">
        <Text>
          {formatTranslation(t("soil.loadingSliderDataFor"))} {propertyName}
          ...
        </Text>
      </div>
    );
  }

  return (
    <div className="zone-color-slider">
      <div
        className="slider-stack"
        style={{
          position: "relative",
          height: "48px", // enough space for both tracks and handles
          width: "100%",
          maxWidth: `${SLIDER_WIDTH}px`,
          display: "flex",
          alignItems: "center",
        }}
      >
        {/* Colored track background */}
        <div
          className="custom-color-track"
          style={{
            position: "absolute",
            top: "50%",
            left: 0,
            right: 0,
            height: "6px",
            transform: "translateY(-50%)",
            backgroundColor: "#f0f0f0",
            borderRadius: "4px",
            zIndex: 1,
          }}
        >
          {renderTrackSegments()}
        </div>

        {/* Slider overlays the colored track */}
        <div style={{ width: "100%" }}>
          <Slider
            range
            min={distributionStats.min}
            max={distributionStats.max}
            value={currentValues.slice(1, -1)}
            onChange={handleSliderChange}
            onAfterChange={handleAfterChange}
            step={thresholdStep}
            tooltip={{
              formatter: () =>
                formatTranslation(t("soil.adjustColorScaleTooltip")),
              placement: "left",
            }}
            style={{
              position: "absolute",
              top: "50%",
              left: 0,
              right: 0,
              width: "100%",
              transform: "translateY(-50%)",
              zIndex: 2,
              background: "transparent",
            }}
            styles={{
              track: {
                backgroundColor: "transparent",
                height: "0px",
                borderRadius: "4px",
              },
              tracks: {
                backgroundColor: "transparent",
              },
              handle: {
                backgroundColor: "transparent",
                top: "1px",
              },
            }}
          />
        </div>
      </div>
    </div>
  );
};
